import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * The blue text link with a trailing arrow — "Start assignment →",
 * "View all →", "View report →". It is the one action a card or a section
 * header carries: blue at rest, one step darker on hover, and the arrow nudges
 * right. Inside a clickable card (a `group` Link) it renders as a span and
 * lights up when any part of the card is hovered; given `href`, it is a Link
 * of its own.
 *
 * This is design-guidelines §7's demotion of the filled button: one solid
 * blue button per visible region, so a grid of cards uses this instead.
 * `tests/unit/sharedRecipes.test.jsx` fails on a hand-rolled copy.
 *
 * Not for: the gray "← Back" link (`BackButton`) or a form's primary action.
 */
export default function ArrowLink({ href, size = "sm", className, children, ...props }) {
  const xs = size === "xs";
  const classes = cn(
    "inline-flex shrink-0 items-center gap-1 font-medium text-action-600 transition-colors hover:text-action-700 group-hover:text-action-700",
    xs ? "text-xs" : "text-sm",
    className,
  );
  const content = (
    <>
      {children}
      <ArrowRight
        className={cn(
          "transition-transform group-hover:translate-x-0.5",
          xs ? "h-3.5 w-3.5" : "h-4 w-4",
        )}
        aria-hidden="true"
      />
    </>
  );
  if (href) {
    return (
      <Link href={href} className={cn("group", classes)} {...props}>
        {content}
      </Link>
    );
  }
  return (
    <span className={classes} {...props}>
      {content}
    </span>
  );
}
